/**
 * Manifest Builder
 *
 * @module @chatsdet/diagnostic-workspace/manifest-builder
 * @description Writes `manifest.json` — the workspace's machine-readable metadata file.
 * The `runStartedAt` field is the canonical baseline for every `T+S.SSSs` relative
 * timestamp in the ledger and per-step rollups.
 */

import * as fs from 'node:fs/promises';
import { resolveWorkspacePath, WORKSPACE_PATHS } from './paths.js';
import type { WorkspaceManifest } from './types.js';
import type { RunDataEvidenceSource } from './run-data.js';

export interface ManifestBuilderInput {
  workspaceRoot: string;
  runId: string;
  workflowId: string;
  orgId: string;
  status: string;
  /** ISO timestamp or null. */
  startedAt: string | null;
  /** ISO timestamp or null. */
  finishedAt: string | null;
  errorMessage: string | null;
  workflowName: string;
  failedStepId: string | null;
  failedNodeId: string | null;
  failedStepName: string | null;
  traceStorageKey: string | null;
  diagnosticsStorageKey: string | null;
  videoStorageKey: string | null;
  partial: boolean;
  evidenceSources?: Record<string, RunDataEvidenceSource>;
}

export const writeManifest = async (input: ManifestBuilderInput): Promise<WorkspaceManifest> => {
  const startedMs = input.startedAt ? Date.parse(input.startedAt) : null;
  const finishedMs = input.finishedAt ? Date.parse(input.finishedAt) : null;
  const durationMs = startedMs !== null && finishedMs !== null ? finishedMs - startedMs : null;

  const manifest: WorkspaceManifest = {
    runId: input.runId,
    workflowId: input.workflowId,
    orgId: input.orgId,
    status: input.status,
    startedAt: input.startedAt,
    finishedAt: input.finishedAt,
    durationMs,
    errorMessage: input.errorMessage,
    workflowName: input.workflowName,
    failedStepId: input.failedStepId,
    failedNodeId: input.failedNodeId,
    failedStepName: input.failedStepName,
    traceStorageKey: input.traceStorageKey,
    diagnosticsStorageKey: input.diagnosticsStorageKey,
    videoStorageKey: input.videoStorageKey,
    materializedAt: new Date().toISOString(),
    partial: input.partial,
    evidenceSources: input.evidenceSources,
  };

  await fs.writeFile(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.manifest),
    `${JSON.stringify(manifest, null, 2)}\n`,
    'utf-8'
  );

  return manifest;
};
